import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import { DEMO_EMPLOYEE } from "../lib/roles";
import {
  ORG_RANK_ORDER,
  SEED_ORG_UNITS,
  buildOrgTree,
  effectiveCeiling,
  filterTreeByVisibility,
  loadVisibilityPolicy,
  personById,
  saveVisibilityPolicy,
  upwardChainIds,
  type OrgChainSide,
  type OrgChartRank,
  type TreeNode,
  type VisibilityPolicyRow,
} from "../lib/orgHierarchy";

function TreeList({
  nodes,
  depth,
  selectedId,
  onSelect,
}: {
  nodes: TreeNode[];
  depth: number;
  selectedId: string | null;
  onSelect: (n: TreeNode) => void;
}) {
  return (
    <ul className={depth === 0 ? "space-y-1" : "mt-1 space-y-1 border-l border-navy-100 pl-3"}>
      {nodes.map((n) => (
        <li key={`${n.kind}-${n.id}`}>
          <button
            type="button"
            onClick={() => onSelect(n)}
            className={`w-full rounded-lg px-2 py-1.5 text-left text-sm ${
              selectedId === n.id
                ? "bg-brand-teal/15 text-navy font-semibold"
                : "hover:bg-navy-50 text-ink-muted"
            }`}
          >
            <span className="text-navy">{n.label}</span>
            <span className="block text-xs text-ink-subtle">{n.sublabel}</span>
          </button>
          {n.children.length > 0 && (
            <TreeList
              nodes={n.children}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

export function OrgHierarchyPage() {
  const { moduleIds, homePath, roleIds } = useAuth();
  const canView =
    moduleIds.includes("org-hierarchy") ||
    moduleIds.includes("hr") ||
    moduleIds.includes("company") ||
    roleIds.some((r) =>
      [
        "ROLE-EMPLOYEE",
        "ROLE-SUPERVISOR",
        "ROLE-PROJECT-MANAGER",
        "ROLE-DIRECTOR",
        "ROLE-VP",
        "ROLE-HR",
        "ROLE-VP-HR",
        "ROLE-COMPANY-ADMIN",
      ].includes(r),
    );

  const isHr =
    roleIds.includes("ROLE-HR") ||
    roleIds.includes("ROLE-VP-HR") ||
    roleIds.includes("ROLE-COMPANY-ADMIN");

  const [side, setSide] = useState<OrgChainSide | "all">("all");
  const [policy, setPolicy] = useState<VisibilityPolicyRow[]>(() => loadVisibilityPolicy());
  const [selected, setSelected] = useState<TreeNode | null>(null);
  const [flash, setFlash] = useState("");
  const [units, setUnits] = useState(() => SEED_ORG_UNITS.map((u) => ({ ...u })));

  const ceiling = useMemo(() => effectiveCeiling(roleIds, policy), [roleIds, policy]);
  const keepIds = useMemo(() => upwardChainIds(DEMO_EMPLOYEE.id), []);

  const tree = useMemo(() => {
    const raw = buildOrgTree(side);
    // Re-bind BU leadership from editable units for detail panel; tree uses SEED —
    // rebuild from seed structure is fine for demo; leadership edits shown in BU panel.
    return filterTreeByVisibility(raw, ceiling, keepIds);
  }, [side, ceiling, keepIds]);

  if (!canView) return <Navigate to={homePath} replace />;

  function savePolicy() {
    saveVisibilityPolicy(policy);
    setFlash("Org chart visibility policy saved (demo local).");
    window.setTimeout(() => setFlash(""), 3000);
  }

  function setBuLeader(unitId: string, field: "assignedVpId" | "assignedDirectorId", personId: string) {
    setUnits((prev) =>
      prev.map((u) =>
        u.id === unitId ? { ...u, [field]: personId || null } : u,
      ),
    );
    setFlash("Business Unit leadership updated (demo session).");
    window.setTimeout(() => setFlash(""), 3000);
  }

  const bus = units.filter((u) => u.type === "business_unit");

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-brand-teal">
          REL-021 · DEC-ORG-001 · DEC-ORG-002
        </p>
        <h1 className="mt-2 font-display text-3xl text-navy">Company hierarchy</h1>
        <p className="mt-2 max-w-3xl text-sm text-ink-muted leading-relaxed">
          Navigable delivery chain (Employee → Supervisor → PM → Director → VP) and back-office
          functional chains. Your view ceiling:{" "}
          <strong className="text-navy">{ceiling.replace("_", " ")}</strong>
          {ceiling !== "unrestricted" ? (
            <span> — nodes above that rank are hidden (your own upward chain stays visible).</span>
          ) : (
            <span> — full tree.</span>
          )}
        </p>
        {flash && (
          <p className="mt-3 rounded-lg bg-brand-teal/10 px-3 py-2 text-sm text-navy">{flash}</p>
        )}
      </header>

      <div className="mb-4 flex flex-wrap gap-2">
        {(
          [
            ["all", "All chains"],
            ["delivery", "Delivery / billable"],
            ["back_office", "Back office"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setSide(id)}
            className={
              side === id
                ? "rounded-lg bg-navy-900 px-3 py-1.5 text-sm font-semibold text-white"
                : "rounded-lg px-3 py-1.5 text-sm font-medium text-ink-muted hover:bg-navy-50"
            }
          >
            {label}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <section className="rounded-xl border border-navy-100 bg-white p-4">
          <h2 className="mb-3 font-display text-lg text-navy">Org tree</h2>
          <TreeList
            nodes={tree}
            depth={0}
            selectedId={selected?.id ?? null}
            onSelect={setSelected}
          />
          {tree.length === 0 && (
            <p className="text-sm text-ink-muted">Nothing visible under your ceiling.</p>
          )}
        </section>

        <aside className="space-y-4">
          <section className="rounded-xl border border-navy-100 bg-white p-4">
            <h2 className="font-display text-lg text-navy">Selection</h2>
            {selected ? (
              <div className="mt-2 text-sm text-ink-muted space-y-1">
                <div className="font-semibold text-navy">{selected.label}</div>
                <div>{selected.sublabel}</div>
                {selected.person?.supervisorId && (
                  <div>
                    Reports to:{" "}
                    {personById(selected.person.supervisorId)?.name ?? "—"}
                  </div>
                )}
                {selected.unit?.type === "business_unit" && (
                  <div className="pt-2 text-xs">
                    BU leadership drives contract attribution defaults.
                  </div>
                )}
              </div>
            ) : (
              <p className="mt-2 text-sm text-ink-muted">Select a node.</p>
            )}
          </section>

          {isHr && (
            <section className="rounded-xl border border-navy-100 bg-white p-4">
              <h2 className="font-display text-lg text-navy">BU leadership</h2>
              <p className="mt-1 text-xs text-ink-subtle mb-3">
                Assign a VP (and Director) to each Business Unit.
              </p>
              <div className="space-y-3">
                {bus.map((u) => (
                  <div key={u.id} className="border-t border-navy-50 pt-2 first:border-0 first:pt-0">
                    <div className="text-sm font-medium text-navy">{u.name}</div>
                    <label className="mt-1 block text-xs">
                      Assigned VP
                      <select
                        className="mt-0.5 w-full rounded border border-navy-100 px-2 py-1"
                        value={u.assignedVpId ?? ""}
                        onChange={(e) => setBuLeader(u.id, "assignedVpId", e.target.value)}
                      >
                        <option value="">—</option>
                        <option value="emp-vp-del">Casey Vogel (VP Delivery)</option>
                        <option value="emp-vp-fin">Alex Rivera (VP Finance)</option>
                      </select>
                    </label>
                    <label className="mt-1 block text-xs">
                      Assigned Director
                      <select
                        className="mt-0.5 w-full rounded border border-navy-100 px-2 py-1"
                        value={u.assignedDirectorId ?? ""}
                        onChange={(e) =>
                          setBuLeader(u.id, "assignedDirectorId", e.target.value)
                        }
                      >
                        <option value="">—</option>
                        <option value="emp-dir-fc">Taylor Brooks (Fed Civ)</option>
                        <option value="emp-dir-dod">Quinn Avery (DoD)</option>
                      </select>
                    </label>
                  </div>
                ))}
              </div>
            </section>
          )}
        </aside>
      </div>

      {isHr && (
        <section className="mt-6 rounded-xl border border-navy-100 bg-white p-5">
          <h2 className="font-display text-xl text-navy">Visibility depth policy</h2>
          <p className="mt-1 text-sm text-ink-muted max-w-2xl">
            HR sets how far each role may browse. Example: Employee/Supervisor stop at Project
            Manager; PM and above see the full company. HR/Admin always unrestricted.
          </p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-navy-50 text-xs uppercase tracking-wide text-ink-subtle">
                <tr>
                  <th className="px-3 py-2">Role</th>
                  <th className="px-3 py-2">Max visible rank</th>
                </tr>
              </thead>
              <tbody>
                {policy.map((row) => (
                  <tr key={row.roleId} className="border-t border-navy-100">
                    <td className="px-3 py-2 text-navy">{row.label}</td>
                    <td className="px-3 py-2">
                      {row.lockedFull ? (
                        <span className="text-ink-subtle">Unrestricted (locked)</span>
                      ) : (
                        <select
                          className="rounded border border-navy-100 px-2 py-1"
                          value={row.ceiling}
                          onChange={(e) =>
                            setPolicy((prev) =>
                              prev.map((p) =>
                                p.roleId === row.roleId
                                  ? { ...p, ceiling: e.target.value as OrgChartRank }
                                  : p,
                              ),
                            )
                          }
                        >
                          {ORG_RANK_ORDER.map((r) => (
                            <option key={r} value={r}>
                              {r.replace(/_/g, " ")}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            onClick={savePolicy}
            className="mt-4 rounded-lg bg-navy-900 px-4 py-2 text-sm font-semibold text-white"
          >
            Save visibility policy
          </button>
        </section>
      )}
    </div>
  );
}
